import React, { useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import TopNav from "../../components/navigation/TopNav";
import TimeRangeSelector from "../../components/helpers/TimeRangeSelector";

import StandardScreen, { ScreenContent } from "../../components/layout/StandardScreen";
import Stack from "../../components/layout/Stack";
import Card from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import FormField from "../../components/forms/FormField";
import StatusMessage from "../../components/ui/StatusMessage";
import {
  TableShell,
  Table,
  Th,
  Td,
  NumberTd,
} from "../../components/data/Table";

const narrowShellStyle = {
  width: "100%",
  margin: "0 auto",
};



export default function PayerInvoiceHistoryReport() {
  const { t } = useTranslation();

  const [summary, setSummary] = useState([]);
  const [message, setMessage] = useState("");
  const [messageVariant, setMessageVariant] = useState("info");
  const [timeRange, setTimeRange] = useState({ start: "", end: "" });

  const fetchReport = async () => {
    if (!timeRange.start || !timeRange.end) return;

    axios.defaults.headers.common["Authorization"] = `Bearer ${localStorage.getItem("token")}`;

    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/payer/invoices-in-time`,
        { params: { start: timeRange.start, end: timeRange.end } }
      );

      setSummary(res.data);

      if (res.data.length === 0) {
        setMessage(t("payer_invoice_history_report.messages.empty"));
        setMessageVariant("info");
      } else {
        setMessage("");
        setMessageVariant("info");
      }
    } catch (err) {
      console.error("Failed to fetch invoice history", err);
      setSummary([]);
      setMessage(t("payer_invoice_history_report.messages.fetch_failed"));
      setMessageVariant("error");
    }
  };

  const downloadInvoicePdf = async (invoice) => {
    try {
      const token = localStorage.getItem("token");

      if (!token) throw new Error("Missing auth token");
      if (!invoice?.invoice_id) throw new Error("Missing invoice_id");
      if (!invoice?.std_invoice_name) throw new Error("Missing std_invoice_name");

      const filename = invoice.std_invoice_name.toLowerCase().endsWith(".pdf")
        ? invoice.std_invoice_name
        : `${invoice.std_invoice_name}.pdf`;

      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/payer/invoices/${invoice.invoice_id}/download`,
        {
          responseType: "blob",
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();

      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Failed to download invoice PDF", err);
      setMessage(t("payer_invoice_history_report.messages.download_failed"));
      setMessageVariant("error");
    }
  };

  return (
    <StandardScreen
      topNav={<TopNav title={t("payer_invoice_history_report.title")} mode="main" />}
    >
      <ScreenContent>
        <div style={narrowShellStyle}>
          <Stack gap="4">

            {/* Filters */}
            <Card title={t("payer_invoice_history_report.title")}>
              <Stack gap="3">
                <FormField>
                  <div>
                    <TimeRangeSelector onRangeChange={setTimeRange} />
                  </div>
                </FormField>

                <Button onClick={fetchReport} fullWidth>
                  {t("payer_invoice_history_report.actions.get_report")}
                </Button>

                <StatusMessage variant={messageVariant} message={message} />
              </Stack>
            </Card>

            {/* Table */}
            {summary.length > 0 && (
              <Card>
                <TableShell maxHeight="65vh">
                  <Table>
                    <thead>
                      <tr>
                        <Th columnIndex={0} sticky wider>{t("payer_invoice_history_report.table.issue_date")}</Th>

                        <Th wider>{t("payer_invoice_history_report.table.std_invoice_name")}</Th>
                        <Th wider align="right"> {t("payer_invoice_history_report.table.tax_inclusive_amount")} </Th>
                        <Th wider>{t("payer_invoice_history_report.table.currency")}</Th>

                        <Th> {t("payer_invoice_history_report.table.raw_invoice_id")} </Th>
                        <Th> {t("payer_invoice_history_report.table.supplier_name")}  </Th>
                        <Th>{t("payer_invoice_history_report.table.supplier_vkn")}</Th>
                        <Th>{t("payer_invoice_history_report.table.customer_name")}</Th>
                        <Th>{t("payer_invoice_history_report.table.customer_vkn")}</Th>
                        <Th>{t("payer_invoice_history_report.table.notes")}</Th>

                        <Th>
                          {t("payer_invoice_history_report.table.download")}
                        </Th>
                      </tr>
                    </thead>

                    <tbody>
                      {summary.map((item, index) => (
                        <tr key={`${item.invoice_id}-${index}`}>

                          <Td columnIndex={0} sticky wrap wider>
                            {new Date(item.issue_date)
                              .toLocaleString("en-GB", {
                                year: "numeric",
                                month: "2-digit",
                                day: "2-digit",
                                hour12: false,
                              })
                              .replace(",", "")}
                          </Td>

                          <Td wider>{item.std_invoice_name || "-"}</Td>
                          <NumberTd wider>{item.tax_inclusive_amount}</NumberTd>
                          <Td wider>{item.currency}</Td>

                          <Td > {item.raw_invoice_id}</Td>
                          <Td>{item.supplier_name}</Td>
                          <Td>{item.supplier_vkn}</Td>
                          <Td>{item.customer_name}</Td>
                          <Td>{item.customer_vkn}</Td>
                          <Td>{item.notes || "-"}</Td>

                          <Td>
                            <Button
                              onClick={() => downloadInvoicePdf(item)}
                              disabled={!item.std_invoice_name}
                            >
                              {t("payer_invoice_history_report.actions.download")}
                            </Button>
                          </Td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </TableShell>
              </Card>
            )}

          </Stack>
        </div>
      </ScreenContent>
    </StandardScreen>
  );
}